import { useState, useRef, useEffect } from 'react';
import { HelpCircle, ArrowLeft, X } from 'lucide-react';

/**
 * InfoTip — small clickable (i) icon that opens a compact tooltip
 * with an Arabic explanation + optional "اعرف أكثر" link to the
 * Learning Center (/learn) targeting a specific section anchor.
 *
 * Usage:
 *   <InfoTip
 *     text="جودة الإشارة — رقم من 0 إلى 100..."
 *     learnSection="gold-hub"
 *     testId="tip-quality"
 *   />
 */
export default function InfoTip({
  text,
  learnSection,
  testId,
  size = 14,
  className = '',
  align = 'right', // 'right' | 'left' | 'center'
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const alignClass =
    align === 'left' ? 'left-0' : align === 'center' ? 'left-1/2 -translate-x-1/2' : 'right-0';

  return (
    <span ref={ref} className={`relative inline-flex align-middle ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        data-testid={testId || 'info-tip'}
        aria-label="معلومات"
        className="inline-flex items-center justify-center rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-400/20 hover:border-cyan-400/60 transition-colors"
        style={{ width: size + 6, height: size + 6 }}
      >
        <HelpCircle style={{ width: size, height: size }} strokeWidth={2.4} />
      </button>

      {open && (
        <span
          dir="rtl"
          className={`absolute top-[calc(100%+6px)] ${alignClass} z-[80] w-64 sm:w-72 rounded-xl border border-cyan-400/30 bg-zinc-950/95 backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,0.6)] p-3`}
          data-testid={`${testId || 'info-tip'}-popover`}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-1.5 left-1.5 text-zinc-500 hover:text-white"
            aria-label="إغلاق"
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="text-[12px] leading-relaxed text-zinc-200 pl-5">
            {text}
          </div>
          {learnSection && (
            <a
              href={`/learn#${learnSection}`}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-cyan-300 hover:text-cyan-200"
              data-testid={`${testId || 'info-tip'}-learn`}
            >
              اعرف أكثر في مركز التعليم
              <ArrowLeft className="w-3 h-3" />
            </a>
          )}
        </span>
      )}
    </span>
  );
}
